import { FC, ReactNode, useContext } from "react";
import { useNavigate } from "react-router";

import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button } from "capitalrx-components-2.0/lib/general";
import { Regular, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { CheckIcon, ClipboardIcon, FileInvoiceDollarIcon, PatientIcon, PrescriptionIcon } from "@/constants/Icons";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton, getThemeURL, isProduction, isPrime } from "@/utils";

import styles from "./LandingPage.module.scss";


export const LandingPage: FC = () => {
  const navigate = useNavigate();
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const { isUserLoggedIn } = useContext(AuthenticationContext);

  const {
    primary_color,
    showMedicalClaims,
    showPA,
    showPharmacy,
    showPatient,
    showBilling,
  } = useContext(ThemeSelectorContext);

  const features = {
    paSearch: true,
    paCreate: true,
    patientTools: !isPrime,
    medicalClaims: !isProduction && !isPrime,
    pharmacyRemittance: !isProduction && !isPrime,
    providerManagement: !isPrime,
  };

  interface FeatureButtonProps {
    readonly children?: ReactNode;
    readonly feature?: keyof typeof features;
  }

  const FeatureFlagger: FC<FeatureButtonProps> = ({ feature, children }: FeatureButtonProps) => {
    if (features[feature]) {
      return children;
    } else {
      return <Button appearance={getThemeButton("secondary", primary_color)} disabled={true} >coming soon</Button>;
    }
  };

  return (

    <div className={styles.LandingPanelContainer}>
      <Stack spacing="medium">

        {showPatient && (
          <div className={styles.LandingSubPanel} data-testid="test__patientTools_subpanel">
            <Layout spacing="medium">
              <Stack alignment="left" spacing="mini">
                <Shelf spacing="small">
                  <PatientIcon color={primary_color} />
                  <Subtitle>Patient Profile</Subtitle>
                </Shelf>
                <Regular>Search by patient to see medical service coverage, accumulations, drug coverage, and prior authorization cases in one location</Regular>
              </Stack>
              <Shelf.Spacer />
              <FeatureFlagger feature="patientTools">
                <Button
                  testId="test__medical_benefits_button"
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={() => navigate(getThemeURL("PATIENT_LOOKUP"))}
                >
                  Find A Patient
                </Button>
              </FeatureFlagger>
            </Layout>
          </div>
        )}

        {showPA && (
          <div className={styles.LandingSubPanel} data-testid="test__priorAuthorizations_subpanel">
            <Layout spacing="medium">
              <Stack alignment="left" spacing="mini">
                <Shelf spacing="small">
                  <ClipboardIcon color={primary_color} />
                  <Subtitle>Prior Authorizations</Subtitle>
                </Shelf>
                <Regular>View your existing cases, add documents, or start / renew a case</Regular>
              </Stack>
              <Shelf.Spacer />
              <Layout spacing="small">
                <FeatureFlagger feature="paSearch">
                  <Button
                    testId="test__priorAuthorizations_find_case"
                    appearance={getThemeButton("secondary", primary_color)}
                    onClick={() => {
                      navigate(getThemeURL("PA_SEARCH"));
                    }}
                  >
                    Find A Case
                  </Button>
                </FeatureFlagger>
                <FeatureFlagger feature="paCreate">
                  <Button
                    testId="test__priorAuthorizations_start_new_case"
                    appearance={getThemeButton("secondary", primary_color)}
                    onClick={() => {
                      navigate(`${getThemeURL("PA_CREATE")}?back_url=${encodeURIComponent(getThemeURL("HOME"))}`);
                    }}
                  >
                    Start/Renew A Case
                  </Button>
                </FeatureFlagger>
              </Layout>
            </Layout>
          </div>
        )}

        {showPharmacy && (
          <div className={styles.LandingSubPanel} data-testid="test__pharmacyTools_subpanel">
            <Layout spacing="medium">
              <Stack alignment="left" spacing="mini">
                <Shelf spacing="small">
                  <PrescriptionIcon color={primary_color} />
                  <Subtitle>Pharmacy Payments</Subtitle>
                </Shelf>
                <Regular>Enter details from your payment to receive remittance payment information files</Regular>
              </Stack>
              <Shelf.Spacer />
              <FeatureFlagger feature="pharmacyRemittance">
                <Button
                  testId="test__pharmacy_remittance_button"
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={() => {
                    navigate(getThemeURL("PHARMACY"));
                  }}
                >
                  Request A File
                </Button>
              </FeatureFlagger>
            </Layout>
          </div>
        )}

        {showMedicalClaims && (
          <div className={styles.LandingSubPanel} data-testid="test__medicalClaims_subpanel">
            <Layout spacing="medium">
              <Stack alignment="left" spacing="mini">
                <Shelf spacing="small">
                  <FileInvoiceDollarIcon color={primary_color} />
                  <Subtitle>Medical Claims & Payments</Subtitle>
                </Shelf>
                {!isUserLoggedIn() ? <Regular>View claims and payment status</Regular> : <Regular>View claim statuses, payment details, and download 835s and EOPs</Regular>}
              </Stack>
              <Shelf.Spacer />
              <FeatureFlagger feature="medicalClaims">
                <Button
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={() => {
                    navigate(getThemeURL("MEDICAL_CLAIMS"));
                  }}
                >
                  Find A Claim
                </Button>
              </FeatureFlagger>
            </Layout>
          </div>
        )}

        {showBilling && (
          <div className={styles.LandingSubPanel} data-testid="test__providerManagement_subpanel">
            <Layout spacing="medium">
              <Stack alignment="left" spacing="mini">
                <Shelf spacing="small">
                  <CheckIcon color={primary_color} />
                  <Subtitle>Provider Profile</Subtitle>
                </Shelf>
                {!isUserLoggedIn() ? <Regular>Enroll to manage contact information, notifications, billing, and payment settings</Regular> : <Regular>Manage contact information, notifications, billing, and payment settings </Regular>}
              </Stack>
              <Shelf.Spacer />
              <FeatureFlagger feature="providerManagement">
                <Button
                  testId="test_billing_enrollment_button"
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={() => navigate(getThemeURL("ACCOUNT"))}
                >
                  {!isUserLoggedIn() ? "Enroll" : "Manage Settings"}
                </Button>
              </FeatureFlagger>
            </Layout>
          </div>
        )}

      </Stack>
    </div>

  );
};

export default LandingPage;
